<script setup>
import {useBaseStore} from "~/stores/base";
import ModuleVoteItem from "~/components/product/ModuleVoteItem.vue";
import {votePage} from "~/api/api.vote";
import ModuleVoteItemSkeleton from "~/components/product/ModuleVoteItemSkeleton.vue";

const BNTOMessage = useNuxtApp().$BNTOMessage
const router = useRouter()
const route = useRoute()

/** emits **/
const emits = defineEmits(["back"])

/** props **/
const props = defineProps({
  swiperSlideIndex: {
    default: 0
  }
})

/** data **/
const pageIndex = ref(1)
const pageSize = ref(9)
const list = ref([])
const isReachBottom = ref(false)
const emptyData = ref({voteInfo: {}})

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const userInfo = computed(() => useBaseStore().getterUserInfo)

/** watch **/
watch(() => props.swiperSlideIndex, async () => {
  if (props.swiperSlideIndex === 1) {
    pageIndex.value = 1
    await getVoteResults()
  }
})

/** methods **/
const addSkeleton = () => {
  const skeletonList = []
  for (let i = 0; i < pageSize.value; i++) {
    skeletonList.push({isLoading: true})
  }
  list.value = pageIndex.value === 1 ? skeletonList : list.value.concat(skeletonList)
}
const removeSkeleton = () => {
  list.value = list.value.filter(item => !item.isLoading)
}
const getVoteResults = async () => {
  addSkeleton()
  const res = await votePage({
    pageIndex: pageIndex.value,
    pageSize: pageSize.value
  })
  removeSkeleton()
  if (!res.result.list) {
    isReachBottom.value = true
    emptyData.value = res.result
    return
  }
  list.value = pageIndex.value === 1 ? res.result.list : list.value.concat(res.result.list)
  isReachBottom.value = !res.result.pageIndex
}
const handleNext = async () => {
  if (isReachBottom.value) return
  window.scrollTo({
    top: 0,
    behavior: "auto"
  })
  pageIndex.value++
  await getVoteResults()
}
const handleDeleted = async (shareId) => {
  pageIndex.value = 1
  await getVoteResults()
}

/** lifecycle **/

</script>

<template>
  <div class="slide-all-products-vote-result-mobile" v-if="isMobile">
    <template v-for="(item, index) in list">
      <module-vote-item-skeleton v-if="item.isLoading" :key="`loading-${index}`"/>
      <module-vote-item v-else :key="index" :vote-data="item" @deleted="handleDeleted"/>
    </template>
    <div class="btn-secondary-small" v-if="!isReachBottom" :class="{'btn-disabled-secondary': isReachBottom}" @click="handleNext">view more</div>
    <div class="empty" v-if="!list.length">
      <div class="tips">{{ emptyData.voteInfo.guideDesc }}</div>
      <video
        :src="emptyData.voteInfo.guideVideoUrl"
        :poster="emptyData.voteInfo.guideImageUrl"
        muted
        autoplay
        playsinline
        loop
        type="video/mp4"
      />
    </div>
  </div>
  <div class="slide-all-products-vote-result-desktop" v-else>
    <template v-for="(item, index) in list">
      <module-vote-item-skeleton v-if="item.isLoading" :key="`loading-${index}`"/>
      <module-vote-item v-else :key="index" :vote-data="item" @deleted="handleDeleted"/>
    </template>
    <div class="btn-secondary-small" v-if="!isReachBottom" :class="{'btn-disabled-secondary': isReachBottom}" @click="handleNext">view more</div>
    <div class="empty" v-if="!list.length">
      <div class="tips">{{ emptyData.voteInfo.guideDesc }}</div>
      <video
        :src="emptyData.voteInfo.guideVideoUrl"
        :poster="emptyData.voteInfo.guideImageUrl"
        muted
        autoplay
        playsinline
        loop
        type="video/mp4"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "src/assets/config";

.slide-all-products-vote-result-mobile {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 50px;
  overflow-y: auto;

  .btn-secondary-small {
    border: 0.5px solid $color-BNTO-beige-dark;
  }

  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;

    font-family: "TWK Lausanne";
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 18px; /* 128.571% */
    letter-spacing: -0.056px;

    video {
      margin-top: 16px;
      width: 100%;
      height: 372px;
    }
  }
}

.slide-all-products-vote-result-desktop {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 50px;
  overflow-y: auto;

  .btn-secondary-small {
    border: 0.5px solid $color-BNTO-beige-dark;
  }

  .empty {
    font-family: "TWK Lausanne";
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 18px; /* 128.571% */
    letter-spacing: -0.056px;

    video {
      margin-top: 16px;
      width: 100%;
      height: 472px;
    }
  }
}
</style>
